import React from 'react';
import I18n from 'i18n-js';
import { get } from 'lodash';

import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import bind from '@reverbdotcom/commons/src/bind';

import { BrandMetaData, NameMetaData, PriceMetaData } from './metadata_helper';
import CSP from './comparison_shopping_page';

interface Props {
  page?: CSP;
  trackPosition?: number;
  stripBrandFromTitle?: boolean;
}

const componentName = 'CSPCard';

export default class CSPCard extends React.Component<Props, null> {
  @bind
  trackClick() {
    const { page } = this.props;
    trackEvent({
      componentName,
      eventName: MParticleEventName.ClickedCSPCard,
      cspId: page.id,
      cspSlug: page.slug,
      brand: '',
      category: '',
      displayStyle: '',
      position: this.props.trackPosition,
      subcategories: '',
    });
  }

  titleWithBrandRemoved() {
    if (!this.props.page) {
      return null;
    }
    if (!this.props.stripBrandFromTitle) {
      return this.props.page.title;
    }

    const brandName = get(this.props, 'page.brand.name');
    if (!brandName) {
      return this.props.page.title;
    }

    const regex = new RegExp(`^${brandName} `, 'i');
    return this.props.page.title.replace(regex, '');
  }

  renderPrices() {
    if (!this.props.page) {
      return null;
    }
    const price = this.props.page.lowestPrice();
    const count =
      this.props.page.usedListingsCount + this.props.page.newListingsCount;

    if (!count) {
      return (
        <div className="csp-card__subtitle">
          {I18n.t('discovery.comparisonShoppingPages.noneListed')}
        </div>
      );
    }

    return (
      <div
        className="csp-card__subtitle"
        itemScope
        itemProp="offers"
        itemType="http://schema.org/AggregateOffer"
      >
        <PriceMetaData
          inventory={{
            usedLowPrice: this.props.page.usedLowPrice,
            usedTotal: this.props.page.usedListingsCount,
            newLowPrice: this.props.page.newLowPrice,
            newTotal: this.props.page.newListingsCount,
          }}
        />
        {I18n.t('discovery.comparisonShoppingPages.priceFromWithCount', {
          count,
          price: price.display,
        })}
      </div>
    );
  }

  getBackgroundImageStyle() {
    if (!this.props.page) {
      return {};
    }
    return { backgroundImage: `url(${this.props.page.horizontalImageHref()})` };
  }

  renderPlaceholderCard() {
    return (
      <div className="csp-card csp-card--placeholder">
        {/* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#">
          <div className="csp-card__image" />
          <div className="csp-card__text">
            <div className="csp-card__title" />
            <div className="csp-card__subtitle" />
          </div>
        </a>
      </div>
    );
  }

  render() {
    if (!this.props.page) {
      return this.renderPlaceholderCard();
    }

    return (
      /* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
      <article
        className="csp-card"
        onClick={this.trackClick}
        itemScope
        itemType="http://schema.org/Product"
      >
        <a href={this.props.page && this.props.page.webLink()}>
          <div
            className="csp-card__image"
            style={this.getBackgroundImageStyle()}
          />
          <div className="csp-card__text">
            <BrandMetaData brandName={get(this.props, 'page.brand.name')} />
            <NameMetaData title={this.props.page.title} />
            <h4 className="csp-card__title" itemProp="model">
              {this.titleWithBrandRemoved()}
            </h4>
            {this.renderPrices()}
          </div>
        </a>
      </article>
    );
  }
}
